import { useEffect, useMemo } from "react";
import { Flex, IconButton } from "@chakra-ui/react";
import ContainerFilter from "../../ContainerFilter";
import { useFiltersLists } from "features/filters/store/lists";
import { useFilters } from "features/filters/store";
import OcupacoesButtons from "../../OcupacoesButtons";
import { Idades, Ocupacoes, TecnologiasItem } from "features/maps/typings";
import CheckboxFiltro from "components/inputs/checkbox-filtro";
import { ButtonClearMapCheckbox } from "../../ButtonLimpar";
import { OcupacaoEnum } from "features/maps/enums";
import LoadingBox from "components/layout/loading-box";
import NotFoundMapFilters from "components/NotFoundMapFilters";
import { FaChevronUp } from "react-icons/fa";
import MapGridButton from "components/MapGridButton";

interface FiltersIdadeDoTalhaoProps {
  isVisible: boolean;
  handleChangeIsVisible: (prop: boolean) => void;
  handleOpenGrid: () => void;
}

function FiltersIdadeDoTalhao({
  handleChangeIsVisible,
  handleOpenGrid,
  isVisible,
}: FiltersIdadeDoTalhaoProps) {
  const { filters, updateFiltroIdadeDoTalhao } = useFilters();
  const { loadings, idadeDoTalhao } = useFiltersLists();
  const { filtroIdadeDoTalhao } = filters;

  useEffect(() => {
    if (
      idadeDoTalhao?.ocupacoes?.length &&
      !filtroIdadeDoTalhao?.ocupacao
    ) {
      const firstOcupacao = idadeDoTalhao.ocupacoes.find((o) => o.enabled);
      if (firstOcupacao) {
        updateFiltroIdadeDoTalhao({
          ocupacao: firstOcupacao.codigo,
          tecnologias: firstOcupacao.tecnologias.map((t) => t.id),
          idades: [],
        });
      }
    }
  }, [idadeDoTalhao, filtroIdadeDoTalhao?.ocupacao, updateFiltroIdadeDoTalhao]);

  const findTecnologiasByOcupacaoSelected = useMemo(
    () =>
      idadeDoTalhao?.ocupacoes.find(
        (item) => item.codigo === filtroIdadeDoTalhao?.ocupacao
      ),
    [idadeDoTalhao, filtroIdadeDoTalhao?.ocupacao]
  );

  const findIdades = useMemo(
    () =>
      idadeDoTalhao?.idades.filter(
        (item) => item.ocupacao === filtroIdadeDoTalhao?.ocupacao
      ),
    [idadeDoTalhao, filtroIdadeDoTalhao?.ocupacao]
  );

  const idadesFiltradas = useMemo(() => {
    if (!findIdades) return [];
    if (filtroIdadeDoTalhao?.tecnologias?.length) {
      const filtradas = findIdades.filter((i) =>
        filtroIdadeDoTalhao.tecnologias.includes(i.tecnologiaId)
      );
      return filtradas.length ? filtradas : findIdades;
    }
    return findIdades;
  }, [findIdades, filtroIdadeDoTalhao?.tecnologias]);

  const removedDuplicateIdades = useMemo(
    () =>
      idadesFiltradas.filter(
        (v, i, a) => a.findIndex((t) => t.idade === v.idade) === i
      ),
    [idadesFiltradas]
  );

  const formattedIdadesFilters = removedDuplicateIdades.map((i) => ({
    color: i.cor,
    idade: i.idade,
    tecnologiaId: i.tecnologiaId,
  }));

  const isCheckedTodos =
    !!filtroIdadeDoTalhao?.idades?.length &&
    filtroIdadeDoTalhao.idades.length === removedDuplicateIdades.length;

  const selectOcupacao = (ocupacaoId: OcupacaoEnum) => {
    const ocupacao = idadeDoTalhao?.ocupacoes.find(
      (item) => item.codigo === ocupacaoId
    );
    if (!ocupacao) return;
    updateFiltroIdadeDoTalhao({
      ocupacao: ocupacao.codigo,
      tecnologias: ocupacao.tecnologias.map((t) => t.id),
      idades: [],
    });
  };

  const onClickOperacao = (idade: Idades) => {
    const selected = filtroIdadeDoTalhao?.idades ?? [];
    const exists = selected.some((i) => i.idade === idade.idade);

    const newList = exists
      ? selected.filter((i) => i.idade !== idade.idade)
      : [...selected, { idade: idade.idade, color: idade.cor, tecnologiaId: idade.tecnologiaId }];

    updateFiltroIdadeDoTalhao({
      ...filtroIdadeDoTalhao,
      idades: newList,
    });
  };

  const updateTecnologiasSelected = (tecnologiaId?: string) => {
    const atual = filtroIdadeDoTalhao?.tecnologias ?? [];
    let novoArray = [...atual];

    if (tecnologiaId) {
      const idx = novoArray.indexOf(tecnologiaId);
      if (idx > -1) {
        if (novoArray.length === 1) return;
        novoArray.splice(idx, 1);
      } else {
        novoArray.push(tecnologiaId);
      }
    } else {
      novoArray = findTecnologiasByOcupacaoSelected?.tecnologias.map((t) => t.id) ?? [];
    }

    const idadesCompativeis =
      filtroIdadeDoTalhao?.idades?.filter((i) =>
        novoArray.includes(i.tecnologiaId)
      ) ?? [];

    updateFiltroIdadeDoTalhao({
      ...filtroIdadeDoTalhao,
      tecnologias: novoArray,
      idades: idadesCompativeis,
    });
  };

  const onClearProdutividades = () => {
    updateFiltroIdadeDoTalhao({
      ...filtroIdadeDoTalhao,
      idades: [],
    });
  };

  if (loadings.idadeDoTalhao) {
    return <LoadingBox isVisible={isVisible} />;
  }

  if (!idadeDoTalhao && !loadings.idadeDoTalhao) {
    return (
      <ContainerFilter containerProps={{ mt: 3, rounded: 8, padding: 4 }}>
        <Flex pl={1} mb={1}>
          <NotFoundMapFilters message="Não foram encontradas ocupações para o filtro selecionado" />
        </Flex>
        <Flex
          padding="5px"
          flexDirection="row-reverse"
          borderTop="1px solid #84848440"
          marginTop="10px"
        >
          <IconButton
            background="none"
            _hover={{}}
            _focus={{}}
            _active={{}}
            icon={<FaChevronUp color="#848484" />}
            aria-label="close-button"
            marginRight="20px"
            onClick={() => handleChangeIsVisible(false)}
          />
        </Flex>
      </ContainerFilter>
    );
  }

  return (
    <ContainerFilter
      containerProps={{ mt: 3, rounded: 8, padding: 4 }}
      isVisible={isVisible}
    >
      <OcupacoesButtons
        onChange={selectOcupacao}
        value={filtroIdadeDoTalhao?.ocupacao!}
        items={idadeDoTalhao?.ocupacoes as Ocupacoes[]}
        tecnologiasValue={filtroIdadeDoTalhao?.tecnologias ?? []}
        tecnologiasItems={
          findTecnologiasByOcupacaoSelected?.tecnologias as TecnologiasItem[]
        }
        onChangeTecnologias={updateTecnologiasSelected}
      />

      {!loadings.produtividade && (
        <>
          <Flex h="1px" bg="gray.200" mt={4} flex={1} />

          <Flex mt={4} flexDir="column" ml={2}>
            {removedDuplicateIdades.length === 0 && (
              <Flex>
                <NotFoundMapFilters message="Não foram encontradas idades para o filtro selecionado" />
              </Flex>
            )}

            <Flex>
              {removedDuplicateIdades.length > 0 && (
                <CheckboxFiltro
                  onClick={() =>
                    updateFiltroIdadeDoTalhao({
                      ...filtroIdadeDoTalhao,
                      idades: isCheckedTodos ? [] : formattedIdadesFilters,
                    })
                  }
                  isChecked={isCheckedTodos}
                  text="Todas"
                  data-testid="menu-filtros-operacoes-executadas-todos"
                />
              )}
              {removedDuplicateIdades.length > 0 && (
                <ButtonClearMapCheckbox onClear={onClearProdutividades} />
              )}
            </Flex>

            {removedDuplicateIdades.map((idade, index) => (
              <CheckboxFiltro
                key={idade.idade + index}
                text={idade.idade.toString()}
                onClick={() => onClickOperacao(idade)}
                color={idade.cor}
                data-testid={`menu-filtro-idade-do-talhao-${idade.idade}`}
                isChecked={!!filtroIdadeDoTalhao?.idades?.some((i) => i.idade === idade.idade)}
              />
            ))}
          </Flex>
        </>
      )}

      <Flex
        padding="5px"
        justifyContent="space-between"
        borderTop="1px solid #84848440"
        alignItems="center"
        paddingTop="15px"
        marginTop="10px"
      >
        <MapGridButton handleOpenGrid={handleOpenGrid} />

        <IconButton
          background="none"
          _hover={{}}
          _focus={{}}
          _active={{}}
          icon={<FaChevronUp color="#848484" />}
          aria-label="close-button"
          marginRight="20px"
          onClick={() => handleChangeIsVisible(false)}
        />
      </Flex>
    </ContainerFilter>
  );
}

export default FiltersIdadeDoTalhao;
